import { ActionPayload, SERP_ANALYTICS_ACTIONS_TYPE } from './actions/actionTypes';

export type SendActionReturnSuccess = { delivered: true; response: Response };
export type SendActionReturnFailure = { delivered: false; action: ActionPayload };

export type SendActionReturnType = SendActionReturnSuccess | SendActionReturnFailure;

export type SendActionType = (action: ActionPayload) => Promise<SendActionReturnType>;

/**
 * Handles sending action analytics requests
 * if the request fails, it will be saved in the local storage
 * and will be sent on another instance creation
 */
export class ActionAnalyticsClient {
    private readonly API_URL_ENDPOINT = 'action' as const;
    private readonly url: string;

    constructor(apiUrl: string) {
        this.url = `${apiUrl}/${this.API_URL_ENDPOINT}`;
    }

    /**
     * Sends action to server
     * If action is delivered successfully, return response
     * If action is not delivered successfully, return action payload
     */
    public async sendAction(actionPayload: ActionPayload): Promise<SendActionReturnType> {
        this.logToConsole(actionPayload);

        const resp = await this.sendActionRequest(actionPayload);
        if (resp) {
            return { delivered: true, response: resp };
        }
        return { delivered: false, action: actionPayload };
    }

    private async sendActionRequest(payload: ActionPayload): Promise<Response | false> {
        try {
            const headers = new Headers();
            headers.set('Content-Type', 'application/json');
            const response = await fetch(this.url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });
            if (!response.ok) {
                throw new Error('ActionAPI request failed');
            }
            return response;
        } catch (error: unknown) {
            console.error('ActionAPI request failed', error);
            return false;
        }
    }

    private logToConsole(action: ActionPayload) {
        console.log(
            `%c ActionAnalyticsClient sending action: ${action.action} ${
                action.action === SERP_ANALYTICS_ACTIONS_TYPE.SEARCH ? action.queryString : ''
            }`,
            'background: #222; color: #bada55'
        );
    }
}
